/**
 * Tiny, dependency-free crash reporter speaking the Sentry "envelope" ingest
 * protocol — enough of it that a Sentry-compatible backend (we target Bugsink)
 * gets useful, symbolicated reports without pulling in the ~40 MB `@sentry/*` +
 * OpenTelemetry tree (server) or ~90 KB gzipped SDK (client).
 *
 * Shared by the browser client and the Bun server; both have `fetch` and
 * `Error.stack`, and env-specific globals are reached through `globalThis` so
 * this one file compiles under either tsconfig.
 *
 * What it does: exceptions/messages with parsed stack frames, environment +
 * release tagging, a breadcrumb ring buffer, a mutable scope (user / tags /
 * contexts), global unhandled-error handlers, optional console.error capture,
 * a small offline retry queue, and `debug_meta` populated from the debug IDs
 * `sentry-cli sourcemaps inject` bakes into the bundle (`_sentryDebugIds`) so
 * Bugsink can map minified frames back to real source lines.
 *
 * What it deliberately omits vs. the real SDK: tracing/replay/performance,
 * auto-instrumentation, session tracking, sampling, PII scrubbing, and the
 * long tail of integrations.
 */

const G = globalThis as any;

// ── Types ────────────────────────────────────────────────────────────────
export type Level = "fatal" | "error" | "warning" | "info" | "debug";

export interface ReporterConfig {
  dsn: string;
  environment?: string;
  release?: string;
  /** "javascript" (browser) or "node" (server/Bun). */
  platform: "javascript" | "node";
  maxBreadcrumbs?: number;
  ignoreErrors?: (string | RegExp)[];
  serverName?: string;
  /** Install window/process unhandled-error handlers (default true). */
  installGlobalHandlers?: boolean;
  /** Wrap console.error so its calls become events — used server-side to catch
   *  Lowlander's swallowed RPC-handler errors (default false). */
  captureConsoleError?: boolean;
  /** Static context blocks attached to every event (e.g. runtime/device info). */
  contexts?: Record<string, Record<string, unknown>>;
}

export interface Reporter {
  captureException(err: unknown, extra?: Record<string, unknown>): void;
  captureMessage(message: string, level?: Level): void;
  setUser(user: { id?: string; username?: string } | null): void;
  setTag(key: string, value: string | number | boolean | undefined): void;
  setContext(key: string, ctx: Record<string, unknown> | null): void;
  addBreadcrumb(message: string, category?: string, level?: Level): void;
}

interface Frame {
  filename: string;
  function: string;
  lineno: number;
  colno: number;
  in_app: boolean;
}

const CLIENT_NAME = "wildhunt-reporter";
const CLIENT_VERSION = "1.0.0";

// ── DSN ──────────────────────────────────────────────────────────────────
function parseDsn(dsn: string) {
  // {scheme}://{publicKey}@{host}{path}/{projectId}
  const m = /^(https?):\/\/([^@]+)@([^/]+)(\/.*?)?\/(\d+)\/?$/.exec(dsn);
  if (!m) throw new Error("Invalid Sentry DSN: " + dsn);
  const [, scheme, publicKey, host, path = "", projectId] = m;
  return {
    publicKey,
    envelopeUrl: `${scheme}://${host}${path}/api/${projectId}/envelope/`,
    dsn,
  };
}

// ── Stack parsing (V8/Bun/Chrome + JSC/Firefox) ────────────────────────────
function parseLoc(loc: string, fn: string): Frame | null {
  // loc = "file:line:col"; file may itself contain ':' (e.g. https://).
  const m = /^(.*):(\d+):(\d+)$/.exec(loc.trim());
  if (!m) return null;
  return { filename: m[1], function: fn || "?", lineno: +m[2], colno: +m[3], in_app: true };
}
function parseStackLine(line: string): Frame | null {
  line = line.trim();
  const at = /^at\s+(.*)$/.exec(line); // V8: "at fn (loc)" | "at loc"
  if (at) {
    let rest = at[1], fn = "?";
    const paren = /^(.*?)\s+\((.*)\)$/.exec(rest);
    if (paren) { fn = paren[1]; rest = paren[2]; }
    return parseLoc(rest, fn);
  }
  const amp = line.lastIndexOf("@"); // JSC/Firefox: "fn@loc"
  if (amp >= 0) return parseLoc(line.slice(amp + 1), line.slice(0, amp) || "?");
  if (/:\d+:\d+$/.test(line)) return parseLoc(line, "?");
  return null;
}
/** Parse an Error.stack into Sentry frames (oldest call first). */
function parseStack(stack: string): Frame[] {
  const frames: Frame[] = [];
  for (const line of stack.split("\n")) {
    const f = parseStackLine(line);
    if (f) frames.push(f);
  }
  return frames.reverse();
}

// ── Debug IDs (source-map symbolication) ───────────────────────────────────
// `sentry-cli sourcemaps inject` adds a snippet to each bundle that records a
// filename→debugId association in `globalThis._sentryDebugIds` (keyed by a stack
// string whose top frame is the injected file). We reconstruct filename→debugId
// and attach a debug_meta image per file referenced by an event's frames.
function filenameToDebugId(): Record<string, string> {
  const map: Record<string, string> = {};
  const raw = G._sentryDebugIds;
  if (!raw) return map;
  for (const stack of Object.keys(raw)) {
    const frames = parseStack(stack);
    const f = frames[frames.length - 1]; // top frame = the injected file
    if (f?.filename) map[f.filename] = raw[stack];
  }
  return map;
}
function debugMetaFor(frames: Frame[]): { images: any[] } | undefined {
  const idMap = filenameToDebugId();
  const images: any[] = [];
  const seen = new Set<string>();
  for (const fr of frames) {
    const id = idMap[fr.filename];
    if (id && !seen.has(fr.filename)) {
      seen.add(fr.filename);
      images.push({ type: "sourcemap", code_file: fr.filename, debug_id: id });
    }
  }
  return images.length ? { images } : undefined;
}

// ── Reporter ───────────────────────────────────────────────────────────────
export function createReporter(config: ReporterConfig): Reporter {
  const dsn = parseDsn(config.dsn);
  const maxBreadcrumbs = config.maxBreadcrumbs ?? 100;
  const ignore = config.ignoreErrors ?? [];

  const scope = {
    user: null as { id?: string; username?: string } | null,
    tags: {} as Record<string, string>,
    contexts: { ...(config.contexts ?? {}) } as Record<string, Record<string, unknown>>,
    breadcrumbs: [] as any[],
  };

  // Small offline queue: envelopes that failed to send are retried on a timer
  // and (in the browser) when connectivity returns.
  const queue: string[] = [];
  const MAX_QUEUE = 30;

  function newEventId(): string {
    try {
      return (G.crypto?.randomUUID?.() as string)?.replace(/-/g, "") || randomHex();
    } catch { return randomHex(); }
  }
  function randomHex(): string {
    let s = "";
    for (let i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16);
    return s;
  }

  function shouldIgnore(text: string): boolean {
    return ignore.some((p) => (typeof p === "string" ? text.includes(p) : p.test(text)));
  }

  function send(envelope: string) {
    // keepalive lets in-flight sends survive a browser navigation/unload.
    try {
      G.fetch(dsn.envelopeUrl, {
        method: "POST",
        headers: {
          "content-type": "application/x-sentry-envelope",
          "x-sentry-auth": `Sentry sentry_version=7, sentry_client=${CLIENT_NAME}/${CLIENT_VERSION}, sentry_key=${dsn.publicKey}`,
        },
        body: envelope,
        keepalive: true,
      }).then(
        (r: any) => { if (!r.ok && r.status !== 429) enqueue(envelope); },
        () => enqueue(envelope),
      );
    } catch { enqueue(envelope); }
  }
  function enqueue(envelope: string) {
    if (queue.length < MAX_QUEUE) queue.push(envelope);
  }
  function flush() {
    const pending = queue.splice(0, queue.length);
    for (const e of pending) send(e);
  }
  try {
    setInterval(flush, 20_000).unref?.();
    if (config.platform === "javascript") G.addEventListener?.("online", flush);
  } catch { /* ignore */ }

  function buildEvent(kind: "exception" | "message", payload: any, level: Level, extra?: Record<string, unknown>) {
    const event: any = {
      event_id: newEventId(),
      timestamp: Date.now() / 1000,
      platform: config.platform,
      level,
      sdk: { name: CLIENT_NAME, version: CLIENT_VERSION },
    };
    if (config.environment) event.environment = config.environment;
    if (config.release) event.release = config.release;
    if (config.serverName) event.server_name = config.serverName;
    if (scope.user) event.user = scope.user;
    if (Object.keys(scope.tags).length) event.tags = { ...scope.tags };
    if (Object.keys(scope.contexts).length) event.contexts = { ...scope.contexts };
    if (scope.breadcrumbs.length) event.breadcrumbs = { values: scope.breadcrumbs.slice() };
    if (extra) event.extra = extra;

    if (kind === "exception") {
      event.exception = { values: [payload] };
      const meta = debugMetaFor(payload.stacktrace?.frames ?? []);
      if (meta) event.debug_meta = meta;
    } else {
      event.message = { formatted: payload };
    }
    return event;
  }

  function dispatch(event: any) {
    const header = JSON.stringify({ event_id: event.event_id, sent_at: new Date(event.timestamp * 1000).toISOString(), dsn: dsn.dsn });
    const item = JSON.stringify({ type: "event" });
    send(`${header}\n${item}\n${JSON.stringify(event)}`);
  }

  function captureException(err: unknown, extra?: Record<string, unknown>) {
    try {
      let type = "Error", value = "", frames: Frame[] = [];
      if (err instanceof Error) {
        type = err.name || "Error";
        value = err.message || String(err);
        if (err.stack) frames = parseStack(err.stack);
      } else {
        value = typeof err === "string" ? err : safeString(err);
      }
      if (shouldIgnore(value)) return;
      const exc: any = { type, value };
      if (frames.length) exc.stacktrace = { frames };
      dispatch(buildEvent("exception", exc, "error", extra));
    } catch { /* never throw from telemetry */ }
  }

  function captureMessage(message: string, level: Level = "info") {
    try {
      if (shouldIgnore(message)) return;
      dispatch(buildEvent("message", message, level));
    } catch { /* ignore */ }
  }

  const reporter: Reporter = {
    captureException,
    captureMessage,
    setUser(user) { scope.user = user; },
    setTag(key, v) {
      if (v === undefined) delete scope.tags[key];
      else scope.tags[key] = String(v);
    },
    setContext(key, ctx) {
      if (ctx === null) delete scope.contexts[key];
      else scope.contexts[key] = ctx;
    },
    addBreadcrumb(message, category = "default", level: Level = "info") {
      scope.breadcrumbs.push({ timestamp: Date.now() / 1000, type: "default", category, message, level });
      if (scope.breadcrumbs.length > maxBreadcrumbs) scope.breadcrumbs.shift();
    },
  };

  // ── Global handlers ──
  if (config.installGlobalHandlers !== false) {
    try {
      if (config.platform === "javascript" && G.addEventListener) {
        G.addEventListener("error", (e: any) => {
          // Resource-load failures (img/script/css 404) also fire 'error' but
          // have a target element and no real error — those aren't JS crashes.
          if (e?.target && e.target !== G && e.target.tagName) return;
          const err = e?.error ?? e?.message;
          if (err) reporter.captureException(err);
        });
        G.addEventListener("unhandledrejection", (e: any) => {
          if (e && "reason" in e) reporter.captureException(e.reason);
        });
      } else if (G.process?.on) {
        G.process.on("uncaughtException", (e: any) => reporter.captureException(e));
        G.process.on("unhandledRejection", (e: any) => reporter.captureException(e));
      }
    } catch { /* ignore */ }
  }

  // ── console.error capture (server: Lowlander swallows RPC errors here) ──
  if (config.captureConsoleError) {
    const orig = console.error.bind(console);
    console.error = (...args: any[]) => {
      try {
        const errArg = args.find((a) => a instanceof Error);
        if (errArg) reporter.captureException(errArg, { console: args.map(safeString) });
        else reporter.captureMessage(args.map(safeString).join(" "), "error");
      } catch { /* ignore */ }
      orig(...args);
    };
  }

  return reporter;
}

function safeString(v: unknown): string {
  if (typeof v === "string") return v;
  if (v instanceof Error) return v.stack || `${v.name}: ${v.message}`;
  try { return JSON.stringify(v); } catch { return String(v); }
}
